/**
 * Ajustes de operación que la API lee en caliente: pausar el procesamiento, links
 * compartidos, aviso en la app, tope de gasto diario, tipo de cambio y detectores
 * de reunión. Cada cambio queda en la auditoría. El proveedor de transcripción, el
 * modelo de las notas y la versión mínima de la extensión son variables de Fly.
 */
import { useState, type ReactNode } from 'react'
import { errorText, useApp, useToast } from '../../components/AppContext'
import { ErrorBox, Skeleton } from '../../components/ui'
import { useAsync, useDocumentTitle } from '../../lib/hooks'
import { Seccion } from './common'

function Interruptor({
  titulo,
  ayuda,
  activo,
  peligro,
  onCambiar
}: {
  titulo: string
  ayuda: ReactNode
  activo: boolean
  peligro?: string
  onCambiar: (v: boolean) => void
}) {
  return (
    <div className="row" style={{ gap: 12, alignItems: 'flex-start' }}>
      <div className="grow">
        <div style={{ fontWeight: 550 }}>{titulo}</div>
        <div className="faint" style={{ fontSize: 12 }}>
          {ayuda}
        </div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={activo}
        aria-label={titulo}
        className={`btn sm ${activo ? '' : 'ghost'}`}
        onClick={() => {
          if (peligro && !confirm(peligro)) return
          onCambiar(!activo)
        }}
      >
        {activo ? 'Encendido' : 'Apagado'}
      </button>
    </div>
  )
}

export function Ajustes() {
  useDocumentTitle('Ajustes · Administración')
  const { api } = useApp()
  const toast = useToast()
  const s = useAsync(() => api.adminSettings(), [api])
  const [detectores, setDetectores] = useState<string | null>(null)

  /** Guarda y avisa; devuelve si salió bien (para no descartar lo editado si falló). */
  async function guardar(patch: Record<string, unknown>, ok: string): Promise<boolean> {
    try {
      await api.adminPatchSettings(patch)
      toast(ok)
      await s.reload()
      return true
    } catch (e) {
      toast(errorText(e), 'error')
      return false
    }
  }

  if (s.error) return <ErrorBox error={s.error} onRetry={() => void s.reload()} />
  if (!s.data) return <Skeleton h={240} />
  const v = s.data
  const texto = (k: string, def = '') => (typeof v[k] === 'string' ? (v[k] as string) : def)
  const numero = (k: string, def: number) => (typeof v[k] === 'number' ? (v[k] as number) : def)

  /** Campo que se guarda al salir de él, si cambió. */
  const campo = (
    k: string,
    etiqueta: string,
    tipo: 'text' | 'number',
    actual: string | number,
    ayuda?: string
  ) => (
    <label className="field">
      <span>{etiqueta}</span>
      <input
        key={`${k}:${actual}`}
        type={tipo}
        defaultValue={actual}
        min={tipo === 'number' ? 0 : undefined}
        step={tipo === 'number' ? 'any' : undefined}
        onBlur={(e) => {
          const bruto = e.target.value.trim()
          const nuevo = tipo === 'number' ? Number(bruto) : bruto
          // Un número vacío, negativo o en 0 no se guarda: `Number('')` es 0 y un tope de
          // US$ 0 dejaría todas las grabaciones en espera (para eso está el interruptor)
          if (
            tipo === 'number' &&
            (bruto === '' || !Number.isFinite(nuevo) || Number(nuevo) <= 0)
          ) {
            toast(`${etiqueta}: escribe un número mayor que 0`, 'error')
            e.target.value = String(actual)
            return
          }
          if (String(nuevo) === String(actual)) return
          void guardar(
            { [k]: tipo === 'text' && nuevo === '' ? null : nuevo },
            `${etiqueta}: guardado`
          )
        }}
      />
      {ayuda && (
        <small className="faint" style={{ fontSize: 12 }}>
          {ayuda}
        </small>
      )}
    </label>
  )

  return (
    <div className="admin-grid">
      <div className="span-6 stack">
        <Seccion titulo="Interruptores">
          <Interruptor
            titulo="Procesamiento"
            ayuda="Apagado: las grabaciones quedan en fila y no se gasta en proveedores."
            activo={!v.processing_paused}
            peligro={
              v.processing_paused ? undefined : '¿Pausar el procesamiento de todas las cuentas?'
            }
            onCambiar={(on) =>
              void guardar(
                { processing_paused: !on },
                on ? 'Procesamiento reanudado' : 'Procesamiento pausado'
              )
            }
          />
          <Interruptor
            titulo="Links compartidos"
            ayuda="Apagado: todos los links públicos dejan de abrir (no se borran)."
            activo={v.shares_enabled !== false}
            peligro={
              v.shares_enabled === false ? undefined : '¿Apagar todos los links compartidos?'
            }
            onCambiar={(on) =>
              void guardar({ shares_enabled: on }, on ? 'Links activados' : 'Links apagados')
            }
          />
        </Seccion>
        <Seccion titulo="Aviso en la app">
          {campo(
            'banner_texto',
            'Texto del aviso',
            'text',
            texto('banner_texto'),
            'Se muestra arriba en la app a todas las personas. Vacío = sin aviso.'
          )}
        </Seccion>
      </div>
      <div className="span-6 stack">
        <Seccion titulo="Proveedores y costos">
          {campo(
            'presupuesto_diario_usd',
            'Tope de gasto diario (US$)',
            'number',
            numero('presupuesto_diario_usd', 25),
            'Al superarlo, las grabaciones nuevas quedan en espera hasta el día siguiente.'
          )}
          {campo(
            'tipo_cambio_clp_usd',
            'Tipo de cambio (CLP por US$)',
            'number',
            numero('tipo_cambio_clp_usd', 950),
            'Solo para mostrar costos en pesos en el panel.'
          )}
        </Seccion>
        <Seccion titulo="Extensión">
          <label className="field">
            <span>Detectores de reunión (JSON)</span>
            <textarea
              rows={6}
              className="mono"
              style={{ fontSize: 12 }}
              value={detectores ?? JSON.stringify(v.detectors ?? {}, null, 2)}
              onChange={(e) => setDetectores(e.target.value)}
            />
          </label>
          <div>
            <button
              className="btn sm"
              disabled={detectores === null}
              onClick={() => {
                try {
                  const parsed: unknown = JSON.parse(detectores ?? '{}')
                  void guardar({ detectors: parsed }, 'Detectores guardados').then((ok) => {
                    if (ok) setDetectores(null)
                  })
                } catch {
                  toast('JSON inválido', 'error')
                }
              }}
            >
              Guardar detectores
            </button>
          </div>
        </Seccion>
        <Seccion titulo="En el servidor (Fly)">
          <p className="faint" style={{ margin: 0, fontSize: 13 }}>
            El proveedor de transcripción (<span className="mono">ASR_PROVIDER</span>), el modelo de
            las notas (<span className="mono">SUMMARY_MODEL</span>), la versión mínima de la
            extensión (<span className="mono">MIN_EXTENSION_VERSION</span>) y el cierre de registros
            (en Supabase → Authentication) no se cambian desde aquí: son configuración del servidor
            y requieren un reinicio.
          </p>
        </Seccion>
      </div>
    </div>
  )
}
